import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { SettingsSection } from '@frontend/settings-ui';
import { theme } from '@frontend/theme';
import { Button, Chip, Heading, Text, useModalControl } from '@frontend/design-system';
import type { AvailabilityMode } from '../../../../../components/availability/availability-form/types';
import { AvailabilitySettingsTrackingIds } from '../../../../../trackingIds/availability-settings-tracking';
import { getAvailabilityRuleTypes } from '../../helpers';
import { useGetAvailabilityRules } from '../../hooks/use-get-availability-rules';
import { ChangeAvailabilityModeModal } from './change-availability-mode-modal';

type Props = {
  currentMode: AvailabilityMode;
  locationId: string;
};

export const AvailabilityModeSection = ({ currentMode, locationId }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const tokens = useTranslationTokens();
  const { modalProps, triggerProps } = useModalControl();
  const { operatory, provider } = getAvailabilityRuleTypes(tokens);
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();

  const { data: availabilityRulesData } = useGetAvailabilityRules({
    locationId,
    searchValue: '',
    availabilityMode: currentMode,
  });

  const existingRulesCount = availabilityRulesData?.reduce((count, rule) => count + (rule.count ?? 0), 0) ?? 0;

  if (!isV3OperatorySupported) return null;

  return (
    <SettingsSection>
      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Heading level={3}>{t('Availability calculated by:')}</Heading>
            {currentMode === 'OPERATORY' && (
              <Chip variant={operatory.iconVariant} leftElement={<Icon name={operatory.icon} size={16} />}>
                {operatory.iconTitle}
              </Chip>
            )}
            {currentMode === 'PROVIDER' && (
              <Chip variant={provider.iconVariant} leftElement={<Icon name={provider.icon} size={16} />}>
                {provider.iconTitle}
              </Chip>
            )}
          </div>
          {currentMode === 'OPERATORY' && (
            <Text size='medium'>
              {t('Schedules are organized by treatment rooms and {{operatories}}', {
                operatories: t(tokens.operatories),
              })}
            </Text>
          )}
          {currentMode === 'PROVIDER' && (
            <Text size='medium'>{t('Schedules are organized by individual providers')}</Text>
          )}
        </div>
        <Button
          variant='tertiary'
          iconName='settings'
          {...triggerProps}
          onClick={(event) => {
            SchedulerV3.trackPendoEvent(AvailabilitySettingsTrackingIds.events.changeAvailabilityModeClicked, {
              locationId,
              currentAvailabilityMode: currentMode === 'OPERATORY' ? 'room' : 'provider',
              numExistingRules: existingRulesCount,
            });
            triggerProps.onClick(event);
          }}
        >
          {t('Change Defaults')}
        </Button>
      </div>
      {modalProps.show && (
        <ChangeAvailabilityModeModal
          currentMode={currentMode}
          modalProps={modalProps}
          locationId={locationId}
          existingRulesCount={existingRulesCount}
        />
      )}
    </SettingsSection>
  );
};
